import { UniDriver } from '@wix/unidriver-core';
import { BaseUniDriver } from '@wix/wix-ui-test-utils/base-driver';
import { SyntheticEventData, Simulate } from 'react-dom/test-utils';

import { baseUniDriverFactory, ReactBase } from '../utils/test-utils/unidriver';
import { statusIndicatorDriverFactory } from '../StatusIndicator/StatusIndicator.uni.driver';
import { StatusIndications } from '../common';

import { dataAttr, dataHooks } from './constants';

export interface InputAreaUniDriver extends BaseUniDriver {
  trigger: (
    trigger: keyof typeof Simulate,
    event: SyntheticEventData,
  ) => Promise<void>;
  focus: () => Promise<void>;
  enterText: (text: string) => Promise<void>;
  getValue: () => Promise<string>;
  getName: () => Promise<string | null>;
  getPlaceholder: () => Promise<any>;
  getDefaultValue: () => Promise<any>;
  getRowsCount: () => Promise<any>;
  getMinLength: () => Promise<any>;
  getMaxLength: () => Promise<any>;
  getTabIndex: () => Promise<any>;
  getReadOnly: () => Promise<any>;
  getResizable: () => Promise<boolean>;
  getDisabled: () => Promise<boolean>;
  getRequired: () => Promise<boolean>;
  getHasCounter: () => Promise<boolean>;
  getCounterValue: () => Promise<string>;
  hasExclamation: () => Promise<boolean>;
  isFocusedStyle: () => Promise<boolean>;
  isSizeSmall: () => Promise<boolean>;
  isHoveredStyle: () => Promise<boolean>;
  isFocus: () => Promise<boolean>;
  getStyle: () => Promise<any>;
  getAriaLabel: () => Promise<string | null>;
  getAriaControls: () => Promise<string | null>;
  getAriaDescribedby: () => Promise<string | null>;
  getTooltipDataHook: () => string;
  getTooltipElement: () => UniDriver;
  getCursorLocation: () => Promise<number>;

  // Status
  hasStatus: (status: StatusIndications) => Promise<boolean>;
  getStatusMessage: () => Promise<string | null>;
}

export const inputAreaUniDriverFactory = (
  base: UniDriver,
  body: UniDriver,
): InputAreaUniDriver => {
  const textArea = base.$('textarea');
  const counterSelector = '[data-hook="counter"]';
  const getStatusIndicatorDriver = () =>
    statusIndicatorDriverFactory(
      base.$(`[data-hook="${dataHooks.tooltip}"]`),
      body,
    );

  const textAreaBase = ReactBase(textArea);

  return {
    ...baseUniDriverFactory(base),

    trigger: (trigger, event) => (textAreaBase as any)[trigger](event),
    focus: () => textAreaBase.focus(),
    enterText: (text: string) => textArea.enterValue(text),
    getValue: () => textArea.value(),
    getName: () => textArea.attr('name'),
    getPlaceholder: () => textArea.prop('placeholder'),
    getDefaultValue: () => textArea.prop('defaultValue'),
    getRowsCount: () => textArea.prop('rows'),
    getMaxLength: () => textArea.prop('maxLength'),
    getMinLength: () => textArea.prop('minLength'),
    getTabIndex: () => textArea.prop('tabIndex'),
    getReadOnly: () => textArea.prop('readOnly'),
    getCursorLocation: () => textArea.prop('selectionStart'),
    getResizable: async () => !!(await base.attr(dataAttr.RESIZABLE)),
    getDisabled: async () =>
      !!(await base.attr(dataAttr.DISABLED)) &&
      !!(await textArea.prop('disabled')),
    getRequired: () => textArea.prop('required'),
    getHasCounter: async () => (await base.$$(counterSelector).count()) > 0,
    getCounterValue: () => base.$(counterSelector).text(),
    hasExclamation: () => getStatusIndicatorDriver().exists(),
    isFocusedStyle: async () => !!(await base.attr(dataAttr.FOCUS)),
    isSizeSmall: async () => (await base.attr(dataAttr.SIZE)) === 'small',
    isHoveredStyle: async () => !!(await base.attr(dataAttr.HOVER)),
    isFocus: () => textAreaBase.isFocus(),
    exists: () => textArea.exists(),
    getStyle: () => textArea.prop('style'),
    getAriaLabel: () => textArea.attr('aria-label'),
    getAriaControls: () => textArea.attr('aria-controls'),
    getAriaDescribedby: () => textArea.attr('aria-describedby'),
    getTooltipDataHook: () => dataHooks.tooltip,
    getTooltipElement: () => base,

    // Status
    /** Return true if there's a status */
    hasStatus: async (status: StatusIndications) => {
      const statusIndicatorDriver = getStatusIndicatorDriver();

      if (await statusIndicatorDriver.exists()) {
        return (
          (await base.attr(dataAttr.STATUS)) === status &&
          (await statusIndicatorDriver.getStatus()) === status
        );
      }

      return (await base.attr(dataAttr.STATUS)) === status;
    },
    /** If there's a status message, returns its text value */
    getStatusMessage: async () => {
      const statusIndicatorDriver = getStatusIndicatorDriver();
      let tooltipText: string | null = null;

      if (await statusIndicatorDriver.hasMessage()) {
        tooltipText = await statusIndicatorDriver.getMessage();
      }

      return tooltipText;
    },
  };
};
